// The search tab's mark: concentric arcs - a fingerprint, an identifier - with
// a lens over one part of them.
//
// It says what the tab does in one picture: the identifier already exists out
// there, and this looks closely at one piece of it. That is why the arcs
// continue OUTSIDE the lens rather than stopping at it.
//
// Two things about the drawing that are load-bearing rather than decorative.
// The lens carries an opaque fill, so the arcs it magnifies read as a separate
// layer instead of doubling up with the ones behind it. And every id is
// per-instance: an SVG id is document-global, so two of these on one page would
// share a clip path and the second would render against the first's geometry.

import { useId } from 'react';
import { cn } from '@/lib/classnames';

export function IntelSearchIcon({ size = 96, className }: { size?: number; className?: string }) {
  const uid = useId().replace(/:/g, '');
  const lensClip = `intel-lens-${uid}`;

  return (
    <svg
      aria-hidden
      className={cn('shrink-0', className)}
      fill="none"
      fillRule="evenodd"
      height={size}
      viewBox="-3 -3 100 100"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <clipPath id={lensClip}>
          <circle cx="26.11" cy="26.11" r="25.716" />
        </clipPath>
      </defs>

      {/* The identifier itself: arcs that carry on past the lens. */}
      <g className="stroke-ink-700" strokeWidth="2">
        <path d="M30.266 30.328c2.068 10.258-.985 20.359-7.397 27.657m.418-26.026c-.014-.072-.022-.144-.036-.217l-.023.004a7.171 7.171 0 1 1 14.06-2.835l-.007.003c2.188 10.85-.314 21.558-6.108 30.025m-7.431-22.483c.077 7.144-2.915 13.85-7.955 18.566m29.054-12.393a46.443 46.443 0 0 1-4.314 14.419" />
        <path d="M45.191 38.127a46.619 46.619 0 0 0-.895-10.627l.002-.002c-1.564-7.754-9.116-12.772-16.87-11.208-7.754 1.563-12.772 9.116-11.209 16.87l.017-.003c1.357 6.731-1.254 13.34-6.22 17.408" />
        <path d="M37.96 10.305c6.619 2.553 11.84 8.31 13.346 15.78h.004a53.467 53.467 0 0 1-.734 24.406M9.337 25.558c1.825-7.977 8.135-14.554 16.678-16.277a21.38 21.38 0 0 1 7.573-.144M8.793 30.046c-.018 1.492.11 3.006.417 4.528l.011-.003c.803 3.98-.705 7.89-3.598 10.341" />
        <path d="M58.895 30.906a28.74 28.74 0 0 0-.565-6.236l-.015.003C55.19 9.176 40.097-.852 24.601 2.273A28.504 28.504 0 0 0 13.525 7.11m-3.444 2.922C3.502 16.584.228 26.203 2.201 35.987l.015-.003c.083.411.175.818.274 1.221" />
      </g>

      {/* Drawn before the lens so the ring covers where it meets the glass. */}
      <path
        className="stroke-ink-200"
        d="M75.3 62.3 88 75"
        strokeLinecap="round"
        strokeWidth="8"
      />

      <g transform="translate(31 18)">
        <circle className="fill-ink-900" cx="26.11" cy="26.11" r="25.716" />
        <g className="stroke-accent-500" clipPath={`url(#${lensClip})`} strokeWidth="2">
          <path d="M-.537 12.531c2.028 10.057-.966 19.96-7.252 27.116m.409-25.517c-.013-.07-.02-.141-.035-.212l-.022.004a7.031 7.031 0 1 1 13.785-2.78l-.007.003C8.486 21.783 6.032 32.28.352 40.582m-7.285-22.043c.075 7.004-2.858 13.58-7.8 18.203M13.753 24.59a45.534 45.534 0 0 1-4.23 14.138" />
          <path d="M14.096 20.178a45.706 45.706 0 0 0-.878-10.42l.002-.001C11.687 2.154 4.282-2.765-3.32-1.233-10.922.3-15.842 7.705-14.309 15.307l.017-.003c1.33 6.6-1.23 13.08-6.1 17.069" />
          <path d="M7.006-7.1c6.49 2.503 11.609 8.147 13.086 15.471h.003a52.421 52.421 0 0 1-.72 23.929M-21.056 7.854c1.789-7.82 7.975-14.27 16.35-15.958a20.962 20.962 0 0 1 7.426-.141m-24.31 20.499a21.121 21.121 0 0 0 .41 4.44l.01-.003a10.544 10.544 0 0 1-3.528 10.139" />
          <path d="M27.532 13.098c.039-2.013-.14-4.06-.554-6.115l-.015.003C23.9-8.207 9.102-18.039-6.09-14.975a27.947 27.947 0 0 0-10.86 4.743m-3.377 2.864c-6.45 6.424-9.66 15.855-7.726 25.447l.015-.003c.082.404.171.802.269 1.198" />
        </g>
        <circle
          className="stroke-ink-200"
          cx="26.11"
          cy="26.11"
          fill="none"
          r="25.716"
          strokeWidth="4"
        />
      </g>
    </svg>
  );
}
